import { useCallback, useMemo, useState } from "react";

type TSortDirection = "asc" | "desc";

type TUseClientTableControlsOptions<TData> = {
  /** The full, already-loaded list to filter and sort in the browser. */
  data: TData[];
  /** Fields to match the search term against (case-insensitive substring). */
  searchFields?: (keyof TData)[];
  /** Custom search predicate (overrides searchFields when provided). */
  searchFn?: (row: TData, term: string) => boolean;
  defaultColumn?: string | null;
  defaultDirection?: TSortDirection;
  /** Extract the comparable value for a given sort column. */
  getSortValue?: (row: TData, column: string) => string | number | null | undefined;
};

type TUseClientTableControlsResult<TData> = {
  rows: TData[];
  search: string;
  setSearch: (value: string) => void;
  sortColumn: string | null;
  sortDirection: TSortDirection;
  handleSort: (column: string) => void;
};

function defaultCompare(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number {
  const av = a ?? "";
  const bv = b ?? "";
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Client-side search + sort for small full-list admin endpoints (backend
 * intentionally left them as bare lists). Loads the full list once, then
 * filters and sorts in the browser.
 */
export function useClientTableControls<TData>({
  data,
  searchFields,
  searchFn,
  defaultColumn = null,
  defaultDirection = "asc",
  getSortValue,
}: TUseClientTableControlsOptions<TData>): TUseClientTableControlsResult<TData> {
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(defaultColumn);
  const [sortDirection, setSortDirection] = useState<TSortDirection>(defaultDirection);

  const handleSort = useCallback((column: string) => {
    setSortColumn((prev) => {
      if (prev === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return column;
    });
  }, []);

  const rows = useMemo(() => {
    let result = data;

    const term = search.trim().toLowerCase();
    if (term) {
      if (searchFn) {
        result = result.filter((row) => searchFn(row, term));
      } else if (searchFields && searchFields.length > 0) {
        result = result.filter((row) =>
          searchFields.some((field) => {
            const value = row[field];
            return value != null && String(value).toLowerCase().includes(term);
          })
        );
      }
    }

    if (sortColumn) {
      const getValue =
        getSortValue ??
        ((row: TData, column: string) =>
          (row as Record<string, unknown>)[column] as string | number | null | undefined);
      result = [...result].sort((a, b) => {
        const cmp = defaultCompare(getValue(a, sortColumn), getValue(b, sortColumn));
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [data, search, searchFields, searchFn, sortColumn, sortDirection, getSortValue]);

  return { rows, search, setSearch, sortColumn, sortDirection, handleSort };
}
